import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Button, Icon, Input, List, Message, Segment } from 'semantic-ui-react';
import { useStore } from '../../app/stores/store';
import { useTranslation } from 'react-i18next';
import { TeamDto, TeamLinkDto } from '../../app/models/Team';

interface Props {
  team: TeamDto;
}

export default observer(function TeamLinksPanel({ team }: Props) {
  const { teamStore } = useStore();
  const { t } = useTranslation();
  const [editMode, setEditMode] = useState(false);
  const [links, setLinks] = useState<TeamLinkDto[]>([]);

  useEffect(() => {
    setLinks(team.teamLinks.map(l => ({ ...l })));
  }, [team.teamLinks]);

  const addLink = () => {
    if (links.length >= 10) return;
    setLinks([...links, { id: '', label: '', url: '', sortOrder: links.length }]);
  };

  const removeLink = (index: number) => {
    setLinks(links.filter((_, i) => i !== index));
  };

  const updateLink = (index: number, field: 'label' | 'url', value: string) => {
    const updated = [...links];
    updated[index] = { ...updated[index], [field]: value };
    setLinks(updated);
  };

  const handleSave = async () => {
    const valid = links.filter(l => l.label.trim() && l.url.trim());
    const ok = await teamStore.updateLinks(team.id, valid);
    if (ok) setEditMode(false);
  };

  // View mode
  if (!editMode) {
    return (
      <Segment>
        {team.teamLinks.length === 0 ? (
          <Message info>
            <Icon name="info circle" />
            {t('teams.links.noLinks')}
          </Message>
        ) : (
          <List divided relaxed>
            {team.teamLinks.map(link => (
              <List.Item key={link.id}>
                <List.Icon name="linkify" size="large" verticalAlign="middle" color="teal" />
                <List.Content>
                  <List.Header
                    as="a"
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {link.label}
                  </List.Header>
                  <List.Description style={{ wordBreak: 'break-all', fontSize: '0.85em', color: '#888' }}>
                    {link.url}
                  </List.Description>
                </List.Content>
              </List.Item>
            ))}
          </List>
        )}
        {team.isCurrentUserCaptain && (
          <Button
            size="small"
            basic
            color="teal"
            onClick={() => setEditMode(true)}
            style={{ marginTop: 12 }}
          >
            <Icon name="edit" />{t('teams.links.add')}
          </Button>
        )}
      </Segment>
    );
  }

  // Edit mode
  return (
    <Segment>
      {links.map((link, i) => (
        <div
          key={i}
          style={{
            display: 'flex',
            gap: 8,
            marginBottom: 10,
            alignItems: 'center',
            flexWrap: 'wrap'
          }}
        >
          <Input
            placeholder={t('teams.links.label')}
            value={link.label}
            onChange={e => updateLink(i, 'label', e.target.value)}
            style={{ flex: '1 1 120px', minWidth: 100 }}
          />
          <Input
            placeholder={t('teams.links.url')}
            value={link.url}
            onChange={e => updateLink(i, 'url', e.target.value)}
            style={{ flex: '3 1 200px', minWidth: 160 }}
          />
          <Button
            icon="trash"
            size="mini"
            basic
            color="red"
            onClick={() => removeLink(i)}
          />
        </div>
      ))}

      {links.length < 10 && (
        <Button
          size="small"
          basic
          color="teal"
          onClick={addLink}
          style={{ marginBottom: 12 }}
        >
          <Icon name="plus" />{t('teams.links.add')}
        </Button>
      )}

      {links.length >= 10 && (
        <Message warning size="small">
          <Icon name="warning" />
          {t('teams.links.maxReached')}
        </Message>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <Button
          positive
          size="small"
          loading={teamStore.submitting}
          disabled={teamStore.submitting}
          onClick={handleSave}
        >
          <Icon name="save" />{t('teams.links.save')}
        </Button>
        <Button
          basic
          size="small"
          onClick={() => {
            setLinks(team.teamLinks.map(l => ({ ...l })));
            setEditMode(false);
          }}
        >
          {t('teams.cancel')}
        </Button>
      </div>
    </Segment>
  );
});